/*
  GlowDeer theme behaviour. Loaded (deferred) on every page from layout/theme.liquid.
  - <glow-slider>        hero slideshow (autoplay, dots, arrows, swipe, pause on hover/focus)
  - <glow-shade-finder>  tabbed shade picker: shade dots + skin tone, one photo
  - .glow-reveal         fade-up on scroll
  - .glow-quotes__rail   drag to scroll on desktop (also the wide product row)
  - <glow-bundle-photos> bundle pages: a photo tile per product, following the picked shades
  - <glow-reel>          customer reel: plays muted while on screen, tap for sound
  - <glow-voice-note>    customer voice-note player (one at a time, waveform seek, speed)
  - <glow-vn-deck>       stacked voice-note cards (swipe, dots, one-time nudge)
  - [data-flying-ribbons] waving ribbons with text running along them (voice notes heading)
  - <glow-finder-toggle> shade finder ribbon bar: tap = red GlowDeer ribbon flies across the screen, finder unfolds
  - <glow-promo-popup>   welcome-offer popup (copy code, auto-apply link, remembered for N days)
  - [data-countdown]     offer countdown band under the hero
  - [data-glow-shade-note] product page shade note follows the selected variant
  - [data-xsell]         cross-sell cards: "+" quick add / shade sheet (<dialog>), opens the cart drawer
  - <glow-sticky-atc>    product page sticky Add to cart bar (presses the main Add to cart button)
  - shade splash         picking a shade spills its colour over the page (View Transitions + paint-splat mask)
*/
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Product page: a shade picked in the shade compare is also picked in the product form. After a variant change
  // Dawn scrolls up to the gallery photo (media-gallery.js, window.scrollTo) and focuses the picked option
  // (product-info.js); for these "quiet" syncs the page stays where the customer is. Ends at the variant change.
  let quietSync = null;
  const startQuietSync = (inputId) => {
    quietSync = { inputId, until: performance.now() + 6000 };
    if (window.scrollTo.glowQuiet) return;
    const nativeScrollTo = window.scrollTo;
    const guarded = function (...args) {
      if (quietSync && performance.now() < quietSync.until) return;
      return nativeScrollTo.apply(window, args);
    };
    guarded.glowQuiet = true;
    window.scrollTo = guarded;
  };
  const endQuietSync = () => {
    if (!quietSync) return;
    const el = document.getElementById(quietSync.inputId); // the re-rendered option Dawn is about to focus
    if (el) el.focus = () => {};
    const done = quietSync;
    setTimeout(() => {
      if (quietSync === done) quietSync = null;
    }, 400);
  };

  /* ---------- Hero slideshow ---------- */
  class GlowSlider extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = Number(this.dataset.interval) || 6000;
      this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.dot), true)));

      ['mouseenter', 'focusin'].forEach((e) => this.addEventListener(e, () => this.stop()));
      ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.start()));

      let startX = null;
      this.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
      this.addEventListener(
        'touchend',
        (e) => {
          if (startX === null) return;
          const dx = e.changedTouches[0].clientX - startX;
          const rtl = document.documentElement.dir === 'rtl';
          if (Math.abs(dx) > 40) this.go(this.index + ((dx < 0) !== rtl ? 1 : -1), true);
          startX = null;
        },
        { passive: true }
      );

      // Theme editor: jump to the slide being edited.
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) {
          this.stop();
          this.go(i);
        }
      });

      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    go(i, userAction = false) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.slides.forEach((slide, j) => {
        const active = j === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button').forEach((el) => (active ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
      });
      this.dots.forEach((dot, j) => {
        dot.classList.toggle('is-active', j === this.index);
        dot.setAttribute('aria-current', j === this.index ? 'true' : 'false');
      });
      if (userAction) this.restart();
    }

    start() {
      if (!this.autoplay || this.timer) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }
  if (!customElements.get('glow-slider')) customElements.define('glow-slider', GlowSlider);

  /* ---------- Shade finder ---------- */
  class GlowShadeFinder extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.selectTab(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const rtl = document.documentElement.dir === 'rtl';
          const step = (e.key === 'ArrowRight') !== rtl ? 1 : -1;
          const next = (i + step + this.tabs.length) % this.tabs.length;
          this.selectTab(next);
          this.tabs[next].focus();
        });
      });
      this.panels.forEach((panel) => this.bindPanel(panel));
    }

    selectTab(i) {
      this.tabs.forEach((tab, j) => {
        tab.classList.toggle('is-active', i === j);
        tab.setAttribute('aria-selected', String(i === j));
      });
      this.panels.forEach((panel, j) => {
        panel.hidden = i !== j;
        panel.classList.toggle('is-active', i === j);
      });
    }

    // Skin tone: one choice for the whole site (every tab, every product page), remembered in the browser.
    static get TONE_KEY() {
      return 'glow-skin-tone';
    }
    readTone() {
      try {
        const t = localStorage.getItem(GlowShadeFinder.TONE_KEY);
        return ['light', 'medium', 'dark'].includes(t) ? t : null;
      } catch (e) {
        return null;
      }
    }
    setTone(tone) {
      try {
        localStorage.setItem(GlowShadeFinder.TONE_KEY, tone);
      } catch (e) {
        /* private mode: the pick just isn't remembered */
      }
      this.panels.forEach((panel) => panel.glowTone?.(tone));
    }

    bindPanel(panel) {
      const swatches = [...panel.querySelectorAll('.glow-swatch')];
      const frame = panel.querySelector('[data-finder-frame]');
      const img = panel.querySelector('[data-finder-img]');
      const tile = panel.querySelector('[data-finder-product]');
      const bottle = panel.querySelector('[data-finder-bottle]');
      const tonesWrap = panel.querySelector('[data-finder-tones]');
      const tones = [...panel.querySelectorAll('[data-tone]')];
      const name = panel.querySelector('[data-finder-name]');
      const note = panel.querySelector('[data-finder-note]');
      const price = panel.querySelector('[data-finder-price]');
      const idInput = panel.querySelector('[data-finder-id]');
      const addBtn = panel.querySelector('[data-finder-add]');
      const form = panel.querySelector('[data-finder-form]');
      const status = panel.querySelector('[data-finder-status]');

      let picked = swatches.find((s) => s.classList.contains('is-active')) || swatches[0];
      let tone = tones.find((t) => t.classList.contains('is-active'))?.dataset.tone || 'medium';

      const swapImage = (url, alt, instant) => {
        if (!img || !url) return;
        img.alt = alt;
        if (img.dataset.current === url) return;
        img.dataset.current = url;
        if (instant) {
          // First paint with a remembered tone: no fade, and the lazy image still loads only when it is seen.
          img.removeAttribute('srcset');
          img.src = url;
          return;
        }
        img.classList.add('is-swapping');
        const pre = new Image();
        pre.onload = () => {
          if (img.dataset.current !== url) return; // a newer pick is already loading
          img.removeAttribute('srcset');
          img.src = url;
          img.classList.remove('is-swapping');
        };
        pre.src = url;
      };

      // The photo for a shade: on the picked skin tone when the shade has one, else its own photo (lips / bottle).
      const toneKey = (t) => `image${t[0].toUpperCase()}${t.slice(1)}`;
      const photoOf = (swatch) => {
        const d = swatch.dataset;
        const onSkin = tones.length ? d[toneKey(tone)] : '';
        return { url: onSkin || d.image, fit: !onSkin && d.fit === 'true', onSkin: Boolean(onSkin) };
      };

      const render = (instant) => {
        if (!picked) return;
        swatches.forEach((s) => {
          s.classList.toggle('is-active', s === picked);
          s.setAttribute('aria-checked', String(s === picked));
        });
        tones.forEach((t) => {
          t.classList.toggle('is-active', t.dataset.tone === tone);
          t.setAttribute('aria-checked', String(t.dataset.tone === tone));
        });
        const d = picked.dataset;
        if (name) name.textContent = d.name;
        if (note) note.textContent = d.note || '';
        if (price) price.textContent = d.price;
        if (idInput) idInput.value = d.variantId;
        if (status) status.textContent = '';
        if (addBtn) {
          const available = d.available === 'true';
          addBtn.disabled = !available;
          addBtn.querySelector('[data-label-add]').hidden = !available;
          addBtn.querySelector('[data-label-sold]').hidden = available;
        }
        // No skin photos for this shade: nothing to pick a tone for.
        if (tonesWrap) tonesWrap.hidden = !(d.imageLight || d.imageMedium || d.imageDark);
        const photo = photoOf(picked);
        const toneLabel = photo.onSkin ? tones.find((t) => t.dataset.tone === tone)?.textContent.trim() : '';
        frame?.classList.toggle('is-fit', photo.fit);
        swapImage(photo.url, toneLabel ? `${d.name} – ${toneLabel}` : d.name, instant);
        // The product tile on the photo shows the picked shade's bottle (not when the frame itself shows it).
        if (tile) {
          tile.hidden = !d.bottle || photo.fit;
          tile.classList.toggle('is-contain', d.bottleFit === 'contain');
          if (d.bottle && bottle && bottle.getAttribute('src') !== d.bottle) bottle.src = d.bottle;
          if (tile.dataset.url) {
            tile.href = `${tile.dataset.url}?variant=${d.variantId}`;
            tile.setAttribute('aria-label', `${tile.dataset.title} – ${d.name}`);
          }
        }
        // Once the visitor is using the finder, have the shade's other tones ready so switching is instant.
        if (this.dataset.touched) {
          ['light', 'medium', 'dark'].forEach((t) => {
            const url = d[toneKey(t)];
            if (url && t !== tone) new Image().src = url;
          });
        }
      };

      // Product page (data-sync-picker): the shade picked here is also picked in the product form above,
      // so the gallery, price and sticky "Add to cart" follow it. Dawn's picker reacts to a bubbling 'change'.
      const syncPicker = this.hasAttribute('data-sync-picker');
      const pickInForm = (name) => {
        const input = [...document.querySelectorAll('product-info variant-selects input[type="radio"]')].find(
          (el) => el.value === name
        );
        if (!input || input.checked) return;
        startQuietSync(input.id);
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          if (swatch === picked) return;
          picked = swatch;
          this.dataset.touched = 'true';
          render();
          if (syncPicker) pickInForm(swatch.dataset.name);
        });
      });
      tones.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.dataset.touched = 'true';
          this.setTone(btn.dataset.tone);
        });
      });
      // The other way round: a shade picked in the product form is shown here.
      panel.glowPick = (variantId) => {
        const swatch = swatches.find((s) => s.dataset.variantId === String(variantId));
        if (!swatch || swatch === picked) return;
        picked = swatch;
        render();
      };
      // Bundle pages: the form's variant is the bundle's, so match by shade name (one of its option values).
      panel.glowPickName = (names) => {
        const swatch = swatches.find((s) => names.includes(s.dataset.name));
        if (!swatch || swatch === picked) return;
        picked = swatch;
        render();
      };
      // A skin tone picked on any tab applies to all of them.
      panel.glowTone = (next, instant) => {
        if (!tones.length || next === tone) return;
        tone = next;
        render(instant);
      };
      if (img && picked) img.dataset.current = photoOf(picked).url || '';
      const remembered = this.readTone();
      if (remembered) panel.glowTone(remembered, true);

      form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!idInput) return;
        addBtn.setAttribute('aria-busy', 'true');
        addBtn.disabled = true;
        try {
          const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(idInput.value), quantity: 1 }], sections: 'cart-icon-bubble' }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || 'error');
          const bubble = document.getElementById('cart-icon-bubble');
          const html = data.sections?.['cart-icon-bubble'];
          if (bubble && html) {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('.shopify-section');
            if (fresh) bubble.innerHTML = fresh.innerHTML;
          }
          status.innerHTML = `${this.dataset.added} <a href="${window.routes?.cart_url || '/cart'}">${this.dataset.viewCart}</a>`;
        } catch (err) {
          status.textContent = err.message && err.message !== 'error' ? err.message : this.dataset.error;
        } finally {
          addBtn.removeAttribute('aria-busy');
          addBtn.disabled = false;
        }
      });
    }
  }
  if (!customElements.get('glow-shade-finder')) customElements.define('glow-shade-finder', GlowShadeFinder);

  /* ---------- Bundle pages: each product's tile shows the shade picked for it ----------
     snippets/glow-bundle-photos.liquid. Tile n belongs to option n of the bundle; its photo map sits in the tile.
     Dawn redraws the picker after every change, so the picked values are read from the page each time. */
  class GlowBundlePhotos extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.tiles = [...this.querySelectorAll('[data-bundle-tile]')].map((tile) => {
        let map = {};
        try {
          map = JSON.parse(tile.querySelector('[data-bundle-map]')?.textContent || '{}');
        } catch (e) {
          /* a broken map just leaves the tile as rendered */
        }
        const t = {
          tile,
          map,
          index: Number(tile.dataset.bundleTile),
          track: tile.querySelector('[data-bundle-track]'),
          dots: tile.querySelector('[data-bundle-dots]'),
          label: tile.querySelector('[data-bundle-shade]'),
          note: tile.querySelector('[data-bundle-note]'),
          notes: [],
          name: null,
        };
        // Swiping: the dots and the small note under the shade name follow the slide in view.
        t.track?.addEventListener('scroll', () => this.mark(t), { passive: true });
        return t;
      });
      // Shade dots under a tile: a tap picks that value in the bundle's own (hidden) picker.
      this.querySelectorAll('[data-bundle-shades] button').forEach((dot) => {
        dot.addEventListener('click', () => {
          const set = document.querySelectorAll('product-info variant-selects fieldset')[Number(dot.parentElement.dataset.bundleShades)];
          const input = [...(set?.querySelectorAll('input[type="radio"]') || [])].find((el) => el.value === dot.dataset.value);
          if (!input || input.checked) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      this.onChange = (e) => {
        if (e.target.closest?.('variant-selects')) this.sync();
      };
      document.addEventListener('change', this.onChange);
      this.sync();
    }
    disconnectedCallback() {
      document.removeEventListener('change', this.onChange);
      this.bound = false;
    }
    // Which slide is in view (works in right-to-left too: scrollLeft is negative there).
    mark(t) {
      const i = Math.round(Math.abs(t.track.scrollLeft) / (t.track.clientWidth || 1));
      [...(t.dots?.children || [])].forEach((dot, n) => dot.classList.toggle('is-on', n === i));
      if (t.note) t.note.textContent = t.notes[i] || '';
    }
    sync() {
      const picked = [...document.querySelectorAll('product-info variant-selects fieldset')].map(
        (set) => set.querySelector('input[type="radio"]:checked')?.value
      );
      this.querySelectorAll('[data-bundle-shades]').forEach((row) => {
        const name = picked[Number(row.dataset.bundleShades)];
        if (!name) return;
        [...row.children].forEach((dot) => {
          dot.classList.toggle('is-on', dot.dataset.value === name);
          dot.setAttribute('aria-checked', String(dot.dataset.value === name));
        });
      });
      this.tiles.forEach((t) => {
        const name = picked[t.index];
        const entry = name && t.map[name];
        if (!entry || !t.track || t.name === name) return;
        const first = t.name === null; // the server already drew this shade's bottle
        t.name = name;
        if (t.label) t.label.textContent = name;
        const title = t.tile.dataset.title || '';
        const slides = [{ src: entry.src, note: '', bottle: true }, ...(entry.more || [])].filter((s) => s.src);
        t.notes = slides.map((s) => s.note || '');
        // Rebuild the slides: the bottle (kept when it is the same photo), then on skin / on the lips.
        const old = t.track.querySelector('.glow-bphotos__slide img');
        const keepBottle = first && old;
        t.track.querySelectorAll('.glow-bphotos__slide').forEach((el, n) => {
          if (!(keepBottle && n === 0)) el.remove();
        });
        slides.forEach((s, n) => {
          if (keepBottle && n === 0) return;
          const slide = document.createElement('div');
          slide.className = 'glow-bphotos__slide';
          if (s.bottle) {
            if (entry.fit === 'contain') slide.classList.add('is-contain');
            if (t.tile.dataset.zoom === 'true') slide.classList.add('is-zoom');
          }
          const img = new Image();
          img.width = 600;
          img.height = 800;
          img.alt = s.note ? `${title} – ${name}, ${s.note}` : `${title} – ${name}`;
          if (!s.bottle) img.loading = 'lazy';
          img.src = s.src;
          slide.appendChild(img);
          t.track.appendChild(slide);
        });
        t.track.scrollLeft = 0;
        if (t.dots) {
          t.dots.textContent = '';
          t.dots.hidden = slides.length < 2;
          slides.forEach((s, n) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.setAttribute('aria-label', `${this.dataset.dotLabel || ''} ${n + 1} / ${slides.length}`.trim());
            dot.addEventListener('click', () => {
              const dir = getComputedStyle(t.track).direction === 'rtl' ? -1 : 1;
              t.track.scrollTo({ left: dir * n * t.track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
            });
            t.dots.appendChild(dot);
          });
        }
        this.mark(t);
      });
    }
  }
  if (!customElements.get('glow-bundle-photos')) customElements.define('glow-bundle-photos', GlowBundlePhotos);

  /* ---------- Reels (sections/glow-ugc.liquid) ----------
     A reel plays silently in a loop while most of it is on screen and stops when it leaves; its file is only
     requested the first time it is seen. A tap turns the sound on (and mutes every other reel) or off again.
     With "reduce motion" or data saver on, nothing plays by itself: the first tap starts the reel with sound. */
  class GlowReel extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-reel-toggle]');
      if (!this.video || this.bound) return;
      this.bound = true;
      this.auto = !reduceMotion && !navigator.connection?.saveData;
      this.video.addEventListener('playing', () => this.classList.add('is-playing'));
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
      // The light file is missing or broken: fall back to Shopify's own copy once.
      this.video.addEventListener('error', () => {
        const fallback = this.video.dataset.fallback;
        if (!fallback || this.video.getAttribute('src') === fallback) return;
        this.video.src = fallback;
        if (this.onScreen) this.video.play()?.catch(() => {});
      });
      // Product moment: pause once per loop at the set second, light the product, then play on.
      this.spotAt = parseFloat(this.dataset.spotTime);
      if (!Number.isNaN(this.spotAt)) {
        const check = () => {
          const t = this.video.currentTime;
          if (t < this.spotAt - 0.3) this.spotDone = false; // the loop started again
          else if (!this.spotDone && !this.spotTimer && !this.video.paused && t >= this.spotAt && t < this.spotAt + 1) this.showSpot();
        };
        // Frame callbacks hit the exact frame; 'timeupdate' (about 4 a second) is the safety net.
        if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
          const frame = () => {
            check();
            this.video.requestVideoFrameCallback(frame);
          };
          this.video.requestVideoFrameCallback(frame);
        }
        this.video.addEventListener('timeupdate', check);
      }
      this.silent = this.hasAttribute('data-silent'); // section switch: no sound at all
      this.toggle?.addEventListener('click', () => {
        if (this.silent) {
          // Visitors can't pause a reel. A tap only starts one that autoplay left stopped (never during a product moment).
          if (this.video.paused && !this.spotTimer) this.play();
          return;
        }
        if (this.spotTimer) {
          this.setSound(this.video.muted); // paused on purpose: a tap only switches the sound
          return;
        }
        if (this.video.paused) {
          this.setSound(true);
          this.play();
        } else this.setSound(this.video.muted);
      });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          ([entry]) => {
            this.onScreen = entry.intersectionRatio >= 0.75;
            if (this.onScreen && this.auto) this.play();
            else if (!this.onScreen) {
              this.endSpot(false);
              this.video.pause();
              this.setSound(false);
            }
          },
          { threshold: [0, 0.75] }
        );
        this.io.observe(this);
      }
    }
    disconnectedCallback() {
      this.io?.disconnect();
    }
    showSpot() {
      this.spotDone = true;
      this.video.pause();
      // Noticed late: step back to the chosen frame, where the ring was placed.
      if (this.video.currentTime - this.spotAt > 0.08) this.video.currentTime = this.spotAt;
      this.classList.add('is-spot');
      this.spotTimer = setTimeout(() => this.endSpot(true), 3200);
    }
    endSpot(resume) {
      if (!this.spotTimer) return;
      clearTimeout(this.spotTimer);
      this.spotTimer = null;
      this.classList.remove('is-spot');
      if (resume && this.onScreen !== false) this.video.play()?.catch(() => {});
    }
    play() {
      if (!this.video.getAttribute('src')) {
        this.video.src = this.video.dataset.src;
        // First run: begin shortly before the product moment (data-start); later loops start at 0.
        const startAt = parseFloat(this.dataset.start);
        if (startAt > 0) {
          const seek = () => {
            if (this.video.currentTime < startAt) this.video.currentTime = startAt;
          };
          if (this.video.readyState >= 1) seek();
          else this.video.addEventListener('loadedmetadata', seek, { once: true });
        }
      }
      this.video.play()?.catch(() => {});
    }
    setSound(on) {
      if (on) {
        document.querySelectorAll('glow-reel').forEach((reel) => reel !== this && reel.setSound?.(false));
        // One sound at a time on the page: a playing voice note stops too.
        document.querySelectorAll('glow-voice-note audio').forEach((audio) => audio.pause());
      }
      this.video.muted = !on;
      this.classList.toggle('has-sound', on);
      if (this.toggle) {
        this.toggle.setAttribute('aria-pressed', String(on));
        this.toggle.setAttribute('aria-label', on ? this.toggle.dataset.labelOff : this.toggle.dataset.labelOn);
      }
    }
  }
  if (!customElements.get('glow-reel')) customElements.define('glow-reel', GlowReel);

  /* ---------- Voice notes ---------- */
  const SPEEDS = [1, 1.5, 2];
  const fmtTime = (s) => {
    s = Math.max(0, Math.round(s || 0));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  class GlowVoiceNote extends HTMLElement {
    connectedCallback() {
      if (this.audio) return;
      this.audio = this.querySelector('audio');
      this.playBtn = this.querySelector('[data-play]');
      this.wave = this.querySelector('[data-wave]');
      this.bars = [...this.wave.children];
      this.time = this.querySelector('[data-time]');
      this.speedBtn = this.querySelector('[data-speed]');
      this.speed = 0;

      const showDuration = () => {
        if (Number.isFinite(this.audio.duration)) this.time.textContent = fmtTime(this.audio.duration);
      };
      this.audio.addEventListener('loadedmetadata', showDuration);
      if (this.audio.readyState >= 1) showDuration();

      this.playBtn.addEventListener('click', () => (this.audio.paused ? this.play() : this.audio.pause()));
      this.audio.addEventListener('play', () => this.setPlaying(true));
      this.audio.addEventListener('pause', () => this.setPlaying(false));
      // Fallback for when animation frames are throttled (background tab, low-power mode).
      this.audio.addEventListener('timeupdate', () => this.render());
      this.audio.addEventListener('ended', () => {
        this.audio.currentTime = 0;
        this.speedBtn.hidden = true;
        this.render();
        showDuration();
      });

      this.speedBtn.addEventListener('click', () => {
        this.speed = (this.speed + 1) % SPEEDS.length;
        this.audio.playbackRate = SPEEDS[this.speed];
        this.speedBtn.textContent = `${SPEEDS[this.speed]}×`;
      });

      // Tap or drag on the waveform to seek; arrow keys jump 5 seconds.
      const seekTo = (clientX) => {
        const d = this.audio.duration;
        if (!Number.isFinite(d)) return;
        const r = this.wave.getBoundingClientRect();
        let p = (clientX - r.left) / r.width;
        if (document.documentElement.dir === 'rtl') p = 1 - p;
        this.audio.currentTime = Math.min(Math.max(p, 0), 1) * d;
        this.render();
      };
      this.wave.addEventListener('pointerdown', (e) => {
        this.wave.setPointerCapture(e.pointerId);
        seekTo(e.clientX);
        const move = (ev) => seekTo(ev.clientX);
        const up = () => {
          this.wave.removeEventListener('pointermove', move);
          this.wave.removeEventListener('pointerup', up);
          this.wave.removeEventListener('pointercancel', up);
        };
        this.wave.addEventListener('pointermove', move);
        this.wave.addEventListener('pointerup', up);
        this.wave.addEventListener('pointercancel', up);
      });
      this.wave.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const rtl = document.documentElement.dir === 'rtl';
        this.audio.currentTime += (e.key === 'ArrowRight') !== rtl ? 5 : -5;
        this.render();
      });
    }

    disconnectedCallback() {
      this.audio?.pause();
    }

    play() {
      // Only one voice note at a time.
      document.querySelectorAll('glow-voice-note').forEach((n) => n !== this && n.audio?.pause());
      this.audio.play().catch(() => {});
    }

    setPlaying(on) {
      this.classList.toggle('is-playing', on);
      this.playBtn.setAttribute('aria-label', on ? this.playBtn.dataset.labelPause : this.playBtn.dataset.labelPlay);
      this.speedBtn.hidden = !on && this.audio.currentTime === 0;
      cancelAnimationFrame(this.raf);
      this.render();
      if (on) {
        const tick = () => {
          this.render();
          this.raf = requestAnimationFrame(tick);
        };
        tick();
      }
    }

    render() {
      const d = this.audio.duration;
      const t = this.audio.currentTime;
      const p = Number.isFinite(d) && d > 0 ? t / d : 0;
      const on = Math.round(p * this.bars.length);
      this.bars.forEach((bar, i) => bar.classList.toggle('is-on', i < on));
      this.wave.setAttribute('aria-valuenow', String(Math.round(p * 100)));
      this.time.textContent = fmtTime(t > 0 ? t : d);
    }
  }
  if (!customElements.get('glow-voice-note')) customElements.define('glow-voice-note', GlowVoiceNote);

  /* Voice-note deck: one card in front, the next two peeking behind. Swipe, arrows or arrow keys. */
  class GlowVnDeck extends HTMLElement {
    connectedCallback() {
      if (this.cards) return;
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.stack = this.querySelector('[data-stack]');
      this.current = this.querySelector('[data-current]');
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.index = 0;
      this.busy = false;
      this.layout();
      if (this.cards.length < 2) return;

      this.initNudge();
      this.addEventListener('keydown', (e) => {
        if (e.target.closest('[data-wave]') || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return;
        const rtl = document.documentElement.dir === 'rtl';
        this.go((e.key === 'ArrowRight') !== rtl ? 1 : -1);
      });

      // Swipe: the front card follows the finger; let go past 60px to change card.
      let startX = null;
      let dx = 0;
      this.stack.addEventListener('pointerdown', (e) => {
        this.touched = true;
        if (this.busy || e.target.closest('[data-wave], button')) return;
        startX = e.clientX;
        dx = 0;
      });
      this.stack.addEventListener('pointermove', (e) => {
        if (startX === null) return;
        dx = e.clientX - startX;
        if (Math.abs(dx) < 6) return;
        const front = this.cards[this.index];
        front.classList.add('is-dragging');
        front.style.transform = `translateX(${dx}px) rotate(${dx / 25}deg)`;
      });
      const release = () => {
        if (startX === null) return;
        startX = null;
        const front = this.cards[this.index];
        front.classList.remove('is-dragging');
        front.style.transform = '';
        if (Math.abs(dx) > 60) this.go(dx < 0 ? 1 : -1);
      };
      this.stack.addEventListener('pointerup', release);
      this.stack.addEventListener('pointercancel', release);
      this.stack.addEventListener('pointerleave', release);

      // Theme editor: bring the selected voice note to the front.
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.cards.indexOf(e.target.closest?.('[data-card]'));
        if (i > -1) {
          this.index = i;
          this.layout();
        }
      });
    }

    layout() {
      const n = this.cards.length;
      this.cards.forEach((card, i) => {
        const pos = (i - this.index + n) % n;
        card.dataset.pos = String(pos);
        card.inert = pos !== 0;
      });
      if (this.current) this.current.textContent = String(this.index + 1);
      this.dots.forEach((dot, i) => dot.classList.toggle('is-on', i === this.index));
    }

    // First time the deck is well in view: the front card half-swipes and springs back, with a tiny buzz
    // on phones that allow it (Android, only after the visitor has tapped the page; iPhones never vibrate).
    initNudge() {
      if (reduceMotion || window.Shopify?.designMode || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          setTimeout(() => this.nudge(), 450);
        },
        { threshold: 0.6 }
      );
      io.observe(this.stack);
    }

    nudge() {
      if (this.touched || this.busy || this.index !== 0) return;
      const cards = [this.cards[0], this.cards[1]].filter(Boolean);
      cards.forEach((c) => c.classList.add('is-nudging'));
      setTimeout(() => {
        try {
          if (navigator.userActivation?.hasBeenActive !== false) navigator.vibrate?.([14, 70, 10]);
        } catch (e) { /* not supported */ }
      }, 380);
      setTimeout(() => cards.forEach((c) => c.classList.remove('is-nudging')), 1450);
    }

    go(step) {
      if (this.busy) return;
      const n = this.cards.length;
      const front = this.cards[this.index];
      front.querySelector('audio')?.pause();
      if (step > 0 && !reduceMotion) {
        // The front card flies off, then everything shifts forward.
        this.busy = true;
        front.classList.add('is-leaving');
        setTimeout(() => {
          front.classList.remove('is-leaving');
          this.index = (this.index + 1) % n;
          this.layout();
          this.busy = false;
        }, 320);
        return;
      }
      this.index = (this.index + step + n) % n;
      this.layout();
    }
  }
  if (!customElements.get('glow-vn-deck')) customElements.define('glow-vn-deck', GlowVnDeck);

  /* Hero teaser: glide to the voice notes and give the card a gold glow. Hidden if the section isn't on the page. */
  const initVoiceJump = (root = document) => {
    root.querySelectorAll('[data-vn-jump]').forEach((link) => {
      if (link.dataset.bound) return;
      link.dataset.bound = 'true';
      const id = (link.getAttribute('href') || '').split('#')[1];
      const target = id && document.getElementById(id);
      if (!target) {
        if (!window.Shopify?.designMode) link.hidden = true;
        return;
      }
      link.addEventListener('click', (e) => {
        e.preventDefault();
        // No #hash in the address: otherwise a refresh jumps back down to the voice notes.
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        const chat = target.querySelector('.glow-vn__card[data-pos="0"]');
        if (!chat) return;
        chat.classList.remove('is-highlight');
        setTimeout(() => chat.classList.add('is-highlight'), reduceMotion ? 0 : 700);
        setTimeout(() => chat.classList.remove('is-highlight'), 2600);
      });
    });
  };

  /* ---------- Welcome offer popup ---------- */
  const storage = {
    get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };

  class GlowPromoPopup extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.key = this.dataset.key || 'glowPromo';
      this.card = this.querySelector('[role="dialog"]');
      this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelector('[data-apply]')?.addEventListener('click', () => this.remember());
      this.querySelector('[data-copy]')?.addEventListener('click', (e) => this.copy(e.currentTarget));
      this.scratch = this.querySelector('[data-scratch]');
      this.brush = this.querySelector('[data-scratch-brush]');
      // Keyboard users can't scratch: tabbing to Copy reveals the code.
      this.querySelector('[data-copy]')?.addEventListener('focus', (e) => {
        if (this.scratch && e.currentTarget.matches(':focus-visible')) this.revealCode();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trapFocus(e);
      });

      if (window.Shopify?.designMode) {
        // Theme editor: open when the section is selected, never on its own.
        document.addEventListener('shopify:section:select', (e) => { if (this.closest('.shopify-section') === e.target) this.open(); });
        document.addEventListener('shopify:section:deselect', (e) => { if (this.closest('.shopify-section') === e.target) this.close(false); });
        return;
      }
      const seen = Number(storage.get(this.key) || 0);
      const days = Number(this.dataset.days) || 3;
      if (seen && Date.now() - seen < days * 864e5 && !this.hasAttribute('data-always')) return; // data-always: testing setting
      setTimeout(() => this.open(), (Number(this.dataset.delay) || 0) * 1000);
    }

    open() {
      if (!this.hidden && this.classList.contains('is-open')) return;
      this.lastFocus = document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('glow-no-scroll'); // page stays put while the popup is open
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      if (this.scratch) {
        setTimeout(() => this.card?.focus({ preventScroll: true }), 350);
        setTimeout(() => this.setupScratch(), reduceMotion ? 50 : 750);
      } else {
        setTimeout(() => this.querySelector('[data-copy]')?.focus({ preventScroll: true }), 350);
      }
    }

    /* ---------- Scratch card: blush over the code, wiped away with the brush ---------- */
    setupScratch() {
      const canvas = this.scratch;
      if (!canvas || this.scratchReady || this.revealed) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width) return;
      this.scratchReady = true;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      this.ctx = ctx;
      this.cw = rect.width;
      this.ch = rect.height;
      this.paintBlush();

      const point = (e) => { const r = canvas.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
      let last = null;
      canvas.addEventListener('pointerdown', (e) => {
        if (this.revealed) return;
        this.demoStop = true;
        try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* synthetic or ended pointer */ }
        last = point(e);
        this.wipe(last, last);
        this.moveBrush(e.clientX, e.clientY, -8);
      });
      canvas.addEventListener('pointermove', (e) => {
        if (this.revealed) return;
        if (e.pointerType === 'mouse' || last) this.moveBrush(e.clientX, e.clientY, last ? -8 : 0);
        if (!last) return;
        const p = point(e);
        this.wipe(last, p);
        last = p;
      });
      const up = () => { if (!last) return; last = null; this.checkScratch(); };
      canvas.addEventListener('pointerup', up);
      canvas.addEventListener('pointercancel', up);
      canvas.addEventListener('pointerleave', () => { up(); this.brush?.classList.remove('is-on'); });

      this.demoStroke();
    }

    paintBlush() {
      const { ctx, cw: w, ch: h } = this;
      ctx.globalCompositeOperation = 'source-over';
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#e9a3a8');
      g.addColorStop(0.45, '#d9636f');
      g.addColorStop(1, '#c44966');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      // soft brush strokes in the blush
      ctx.lineCap = 'round';
      for (let i = 0; i < 7; i++) {
        ctx.strokeStyle = i % 2 ? 'rgba(255, 214, 214, 0.22)' : 'rgba(170, 40, 70, 0.16)';
        ctx.lineWidth = 6 + Math.random() * 10;
        ctx.beginPath();
        const y = h * (0.15 + Math.random() * 0.7);
        ctx.moveTo(-10, y);
        ctx.bezierCurveTo(w * 0.3, y - 12 + Math.random() * 24, w * 0.7, y - 12 + Math.random() * 24, w + 10, y + Math.random() * 10 - 5);
        ctx.stroke();
      }
      // gold shimmer
      for (let i = 0; i < Math.round(w * h / 90); i++) {
        ctx.fillStyle = Math.random() > 0.5 ? 'rgba(255, 236, 190, 0.55)' : 'rgba(255, 255, 255, 0.4)';
        const r = Math.random() * 1.1 + 0.3;
        ctx.beginPath();
        ctx.arc(Math.random() * w, Math.random() * h, r, 0, Math.PI * 2);
        ctx.fill();
      }
      const hint = this.scratch.dataset.hint;
      if (hint) {
        ctx.fillStyle = 'rgba(255, 246, 240, 0.95)';
        ctx.font = '600 12px ' + getComputedStyle(document.body).fontFamily;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(120, 20, 40, 0.35)';
        ctx.shadowBlur = 3;
        ctx.fillText(('✦  ' + hint + '  ✦').toUpperCase(), w / 2, h / 2 + 0.5);
        ctx.shadowBlur = 0;
      }
    }

    wipe(a, b, width = 26) {
      const ctx = this.ctx;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = '#000'; // must be opaque: destination-out erases by the stroke's alpha
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(b[0] + 0.01, b[1]);
      ctx.stroke();
    }

    moveBrush(clientX, clientY, tilt = 0) {
      if (!this.brush || !this.card) return;
      const r = this.card.getBoundingClientRect();
      this.brush.style.setProperty('--bx', clientX - r.left + 'px');
      this.brush.style.setProperty('--by', clientY - r.top + 'px');
      this.brush.style.setProperty('--br', tilt + 'deg');
      this.brush.classList.add('is-on');
    }

    // One short, thin stroke on the left when the popup opens: shows the blush can be brushed away without giving the code away.
    demoStroke() {
      const c = this.scratch.getBoundingClientRect();
      const y = this.ch * 0.66;
      if (reduceMotion) { this.wipe([this.cw * 0.07, y], [this.cw * 0.3, y], 12); return; }
      const from = this.cw * 0.06, to = this.cw * 0.32, dur = 900;
      let start = null;
      let prev = [from, y];
      const step = (now) => {
        if (this.demoStop || this.revealed) { this.brush?.classList.remove('is-on'); return; }
        start = start || now;
        const k = Math.min(1, (now - start) / dur);
        const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
        const p = [from + (to - from) * e, y + Math.sin(e * Math.PI * 2) * (this.ch * 0.08)];
        this.wipe(prev, p, 12);
        prev = p;
        this.moveBrush(c.left + p[0], c.top + p[1], -10 + Math.sin(e * Math.PI) * 8);
        if (k < 1) requestAnimationFrame(step);
        else setTimeout(() => { if (!this.demoStop) this.brush?.classList.remove('is-on'); }, 350);
      };
      requestAnimationFrame(step);
    }

    checkScratch() {
      const { ctx } = this;
      const { width, height } = this.scratch;
      const data = ctx.getImageData(0, 0, width, height).data;
      let clear = 0, n = 0;
      for (let i = 3; i < data.length; i += 4 * 24) { n++; if (data[i] < 40) clear++; }
      if (clear / n > 0.5) this.revealCode();
    }

    revealCode() {
      if (this.revealed) return;
      this.revealed = true;
      this.demoStop = true;
      this.scratch?.classList.add('is-done');
      this.brush?.classList.remove('is-on');
    }

    close(remember = true) {
      if (remember) this.remember();
      this.classList.remove('is-open');
      document.documentElement.classList.remove('glow-no-scroll');
      setTimeout(() => { this.hidden = true; }, reduceMotion ? 0 : 450);
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    remember() {
      storage.set(this.key, String(Date.now()));
    }

    async copy(btn) {
      const code = this.querySelector('[data-code]')?.textContent.trim() || '';
      let ok = false;
      try {
        await navigator.clipboard.writeText(code);
        ok = true;
      } catch (e) {
        // Older browsers: select a temporary field and copy.
        const t = document.createElement('textarea');
        t.value = code;
        t.setAttribute('readonly', '');
        t.style.cssText = 'position:fixed;opacity:0';
        document.body.appendChild(t);
        t.select();
        try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
        t.remove();
      }
      if (!ok) {
        // Copy blocked: select the code so it can be copied by hand.
        const range = document.createRange();
        range.selectNodeContents(this.querySelector('[data-code]'));
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        return;
      }
      const label = btn.textContent;
      btn.textContent = btn.dataset.labelCopied || 'Copied';
      btn.classList.add('is-copied');
      this.remember();
      setTimeout(() => { btn.textContent = label; btn.classList.remove('is-copied'); }, 2200);
    }

    trapFocus(e) {
      const f = [...this.card.querySelectorAll('a[href], button:not([disabled])')].filter((el) => el.offsetParent);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  if (!customElements.get('glow-promo-popup')) customElements.define('glow-promo-popup', GlowPromoPopup);

  /* ---------- Offer countdown band ---------- */
  const initCountdowns = (root = document) => {
    root.querySelectorAll('[data-countdown]').forEach((band) => {
      if (band.dataset.bound) return;
      band.dataset.bound = 'true';
      let end = Date.parse(band.dataset.end);
      const section = band.closest('.shopify-section') || band;
      if (Number.isNaN(end)) { section.hidden = true; return; }
      // Restart: once the end has passed, move it forward in whole periods (same moment for every visitor).
      const period = (parseInt(band.dataset.restartDays, 10) || 0) * 86400000;
      const roll = () => {
        if (period && end <= Date.now()) end += Math.ceil((Date.now() - end + 1) / period) * period;
      };
      roll();
      const el = {};
      band.querySelectorAll('[data-cd]').forEach((b) => { el[b.dataset.cd] = b; });
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        roll();
        const left = end - Date.now();
        if (left <= 0) {
          clearInterval(band.timer);
          if (!window.Shopify?.designMode) section.hidden = true; // offer over: hide the band
          return;
        }
        const s = Math.floor(left / 1000);
        if (el.d) el.d.textContent = pad(Math.floor(s / 86400));
        if (el.h) el.h.textContent = pad(Math.floor((s % 86400) / 3600));
        if (el.m) el.m.textContent = pad(Math.floor((s % 3600) / 60));
        if (el.s) el.s.textContent = pad(s % 60);
      };
      tick();
      band.timer = setInterval(tick, 1000);
    });
  };

  /* ---------- Product page: gold "Find my shade" button on the product photo ----------
     Rendered by sections/glow-shade-finder.liquid (so it only exists when the page has a shade finder);
     placed on the top corner of the photo here. A tap scrolls down to the finder. */
  // A soft page-turn: a short burst of filtered noise whose pitch sweeps up, like paper sliding past.
  // Made with Web Audio, so no file is downloaded. Only ever called from a tap (browsers require that).
  let swishCtx = null;
  const paperSwish = (delay = 0, level = 0.14, length = 0.36) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      swishCtx = swishCtx || new AC();
      const ctx = swishCtx;
      if (ctx.state === 'suspended') ctx.resume();
      const t0 = ctx.currentTime + delay;
      const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * length), ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (0.55 + 0.45 * Math.random());
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.Q.value = 0.9;
      band.frequency.setValueAtTime(1100, t0);
      band.frequency.exponentialRampToValueAtTime(4200, t0 + length * 0.7);
      const high = ctx.createBiquadFilter();
      high.type = 'highpass';
      high.frequency.value = 600;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(level, t0 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + length);
      noise.connect(band).connect(high).connect(gain).connect(ctx.destination);
      noise.start(t0);
      noise.stop(t0 + length);
    } catch (e) {
      /* no sound is fine */
    }
  };

  const initFinderJump = (root = document) => {
    root.querySelectorAll('[data-finder-jump]').forEach((btn) => {
      const host = document.querySelector('.product__media-wrapper');
      const target = document.getElementById('shade-finder');
      if (!host || !target || btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      // Bundle pages: under the photo tiles. Single products: in the product info, right under the shade dots
      // and the shade note (Abbas, 2026-10-11: on the corner of the photo it was in the way).
      const inline = document.querySelector('product-info .glow-shade-note') || document.querySelector('product-info variant-selects');
      if (!document.querySelector('glow-bundle-photos') && inline) {
        btn.classList.add('glow-finder-jump--inline');
        inline.after(btn);
      } else {
        host.style.position = 'relative';
        host.appendChild(btn);
      }
      btn.hidden = false;
      // The panel's content (antlers + title) comes with the button, in a <template>.
      const wipeContent = document.querySelector('[data-finder-wipe]');
      let busy = false;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (reduceMotion) {
          target.scrollIntoView({ block: 'start' });
          return;
        }
        if (busy) return;
        busy = true;
        // Thrown up from the bottom edge until it covers the screen...
        const wipe = document.createElement('div');
        wipe.className = 'glow-wipe';
        wipe.setAttribute('aria-hidden', 'true');
        if (wipeContent) wipe.appendChild(wipeContent.content.cloneNode(true));
        document.body.appendChild(wipe);
        wipe.getBoundingClientRect(); // start from the bottom before the first move
        wipe.classList.add('is-up');
        if (btn.hasAttribute('data-sound')) {
          paperSwish(0, 0.14, 0.36); // the page comes up...
          paperSwish(0.52, 0.08, 0.3); // ...and turns away
        }
        setTimeout(() => {
          // ...the page jumps to the finder behind it...
          target.scrollIntoView({ behavior: 'instant', block: 'start' });
          setTimeout(() => {
            // ...and it carries on up and away.
            wipe.classList.remove('is-up');
            wipe.classList.add('is-gone');
            setTimeout(() => {
              wipe.remove();
              busy = false;
            }, 400);
          }, 180);
        }, 380);
      });
    });
  };

  /* ---------- Buttons that scroll to a block on the same page (a[data-glow-scroll] href="#id") ---------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest?.('a[data-glow-scroll]');
    const id = link?.getAttribute('href') || '';
    if (!id.startsWith('#') || id.length < 2) return;
    const target = document.getElementById(id.slice(1));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  });

  /* ---------- Reveal on scroll ---------- */
  const initReveal = (root = document) => {
    const items = root.querySelectorAll('.glow-reveal:not(.is-visible)');
    if (reduceMotion || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    items.forEach((el) => io.observe(el));
  };

  /* ---------- Drag to scroll (quote cards, wide product row) ---------- */
  const initDragRails = (root = document) => {
    root.querySelectorAll('.glow-quotes__rail, .glow-wide__rail.is-multi').forEach((rail) => {
      if (rail.dataset.dragBound) return;
      rail.dataset.dragBound = 'true';
      let down = false;
      let startX = 0;
      let startScroll = 0;
      rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true;
        startX = e.clientX;
        startScroll = rail.scrollLeft;
        rail.classList.add('is-dragging');
      });
      window.addEventListener('pointerup', () => {
        down = false;
        rail.classList.remove('is-dragging');
      });
      rail.addEventListener('pointermove', (e) => {
        if (!down) return;
        rail.scrollLeft = startScroll - (e.clientX - startX);
      });
    });
  };

  /* ---------- Product page: shade note, shade compare and sticky bar follow the chosen variant ---------- */
  const initShadeNotes = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      endQuietSync();
      const variant = event?.data?.variant;
      const id = String(variant?.id || '');
      document.querySelectorAll('[data-glow-shade-note]').forEach((wrap) => {
        wrap.querySelectorAll('[data-variant-id]').forEach((p) => (p.hidden = p.dataset.variantId !== id));
      });
      if (!id) return;
      // A shade without its own photo yet (e.g. Peachy, 2026-10-10): Dawn leaves the previous shade's bottle in
      // front, which would show the wrong colour. Keep only the photos the server lists for this shade (the arm
      // photos) and slide to the first of them. Shades with a photo show everything again.
      const gallery = document.querySelector("media-gallery");
      if (gallery) {
        // (Also for shades with a photo: shade-only extras like the on-lips photos; Dawn syncs the big photos itself
        // but not the thumbnails.)
        // With a shade photo, Dawn has already synced the big photos (it moves them out of event.data.html, so that
        // copy is empty by now): the page's own list is the truth. Without one, Dawn does nothing: read the server copy.
        const keepFrom = variant.featured_media
          ? gallery.querySelectorAll(".product__media-list > li[data-media-id]")
          : event.data.html?.querySelectorAll("media-gallery li[data-media-id]") || [];
        const keep = new Set([...keepFrom].map((li) => li.dataset.mediaId));
        let first = null;
        gallery.querySelectorAll(".product__media-list > li[data-media-id]").forEach((li) => {
          const hide = !!keep && keep.size > 0 && !keep.has(li.dataset.mediaId);
          li.classList.toggle("glow-media-off", hide);
          if (!hide && !first) first = li;
        });
        gallery.querySelectorAll(".thumbnail-list > li[data-target]").forEach((li) => {
          li.classList.toggle("glow-media-off", !!keep && keep.size > 0 && !keep.has(li.dataset.target));
        });
        if (!variant.featured_media && keep.size > 0 && first) first.parentElement.scrollTo({ left: first.offsetLeft - first.parentElement.offsetLeft, behavior: "smooth" });
      }
      // Swatch point: the pulsing point on the arm photo moves to the picked shade (snippets/product-thumbnail.liquid).
      document.querySelectorAll("[data-glow-swatch-point]").forEach((dot) => {
        let pts = {};
        try { pts = JSON.parse(dot.dataset.points || "{}"); } catch (e) {}
        const p = pts[id];
        dot.hidden = !p;
        if (p) { dot.style.left = p[0] + "%"; dot.style.top = p[1] + "%"; }
      });
      // First pick: the shade's white photo (held back so the gallery opens on the lifestyle photos) shows now;
      // Dawn has already moved it to the front.
      document.querySelectorAll('.glow-media-held').forEach((el) => el.classList.remove('glow-media-held'));
      document.querySelectorAll('glow-shade-finder[data-sync-picker] [role="tabpanel"]').forEach((panel) => {
        panel.glowPick?.(id);
        if ((variant.options || []).length > 1) panel.glowPickName?.(variant.options);
      });
      document.querySelectorAll('glow-sticky-atc').forEach((bar) => bar.update?.(id));
    });
  };

  // Old links/bookmarks may still end in #glow-voice-notes (added by an earlier version of the teaser).
  // That anchor no longer exists; tidy it out of the address so a refresh starts at the top.
  if (location.hash === '#glow-voice-notes') history.replaceState(null, '', location.pathname + location.search);

  /* ---------- Shade finder as a red ribbon bar: tap = a GlowDeer ribbon flies across the screen and the finder unfolds ---------- */
  class GlowFinderToggle extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.btn = this.querySelector('[data-finder-bar]');
      this.section = this.closest('.glow-finder');
      this.body = document.getElementById(this.btn?.getAttribute('aria-controls') || '');
      this.hint = this.querySelector('.glow-finder__bar-hint');
      this.btn?.addEventListener('click', () => {
        if (this.section.classList.contains('is-open')) this.setOpen(false);
        else this.open();
      });
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', (e) => {
          if (this.closest('.shopify-section') === e.target) this.setOpen(true);
        });
      }
    }

    setOpen(on) {
      this.section.classList.toggle('is-open', on);
      this.btn.setAttribute('aria-expanded', String(on));
      if (this.body) this.body.inert = !on;
      if (this.hint) this.hint.textContent = on ? this.hint.dataset.hintClose : this.hint.dataset.hintOpen;
    }

    open() {
      if (this.busy) return;
      if (reduceMotion) { this.setOpen(true); return; }
      this.busy = true;
      this.fly();
      setTimeout(() => {
        this.setOpen(true);
        const y = this.btn.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }, 520);
      setTimeout(() => { this.busy = false; }, 1600);
    }

    // A wide red satin ribbon (gold edges, GLOWDEER wordmark) unrolls across the screen along an S-curve
    // through the bar, then flies off; gold stars burst from the bar as the finder opens.
    fly() {
      const NS = 'http://www.w3.org/2000/svg';
      const W = window.innerWidth;
      const H = window.innerHeight;
      const r = this.btn.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const rw = Math.round(Math.max(56, Math.min(W, H) * 0.17));
      const d = `M ${-0.3 * W} ${cy + 0.5 * H} C ${0.15 * W} ${cy - 0.6 * H}, ${0.5 * W} ${cy + 0.55 * H}, ${0.78 * W} ${cy - 0.02 * H} S ${1.2 * W} ${cy - 0.55 * H}, ${1.4 * W} ${cy - 0.3 * H}`;
      const svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('class', 'glow-ribbon-fly');
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      svg.setAttribute('aria-hidden', 'true');
      const word = 'GLOWDEER  ✦  '.repeat(40);
      svg.innerHTML = `
        <defs>
          <linearGradient id="gfrRed" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="${W}" y2="${H}">
            <stop offset="0" stop-color="#7e0f20"/><stop offset="0.5" stop-color="#c42d45"/><stop offset="1" stop-color="#7e0f20"/>
          </linearGradient>
          <path id="gfrPath" d="${d}"/>
          <mask id="gfrMask" maskUnits="userSpaceOnUse" x="${-W}" y="${-H}" width="${3 * W}" height="${3 * H}">
            <path class="gfr-reveal" d="${d}" fill="none" stroke="#fff" stroke-width="${rw + 24}"/>
          </mask>
        </defs>
        <g mask="url(#gfrMask)" style="filter: drop-shadow(0 10px 14px rgba(46,6,12,.35))">
          <use href="#gfrPath" fill="none" stroke="#e2c98f" stroke-width="${rw + 8}"/>
          <use href="#gfrPath" fill="none" stroke="url(#gfrRed)" stroke-width="${rw}"/>
          <use href="#gfrPath" fill="none" stroke="rgba(255,235,225,.16)" stroke-width="${Math.round(rw * 0.3)}" transform="translate(0 ${-Math.round(rw * 0.2)})"/>
          <text font-size="${Math.round(rw * 0.3)}" dy="${Math.round(rw * 0.1)}"><textPath href="#gfrPath" class="gfr-text">${word}</textPath></text>
        </g>`;
      document.body.appendChild(svg);
      const reveal = svg.querySelector('.gfr-reveal');
      const L = reveal.getTotalLength();
      reveal.style.strokeDasharray = `${L} ${L}`;
      reveal.animate(
        [{ strokeDashoffset: L }, { strokeDashoffset: 0, offset: 0.48 }, { strokeDashoffset: 0, offset: 0.56 }, { strokeDashoffset: -L }],
        { duration: 1500, easing: 'cubic-bezier(.5,0,.3,1)', fill: 'forwards' }
      );
      // the wordmark slides along the ribbon while it flies
      const tp = svg.querySelector('.gfr-text');
      const t0 = performance.now();
      const run = (now) => {
        if (!svg.isConnected) return;
        tp.setAttribute('startOffset', String(-((now - t0) * 0.25)));
        requestAnimationFrame(run);
      };
      requestAnimationFrame(run);
      setTimeout(() => {
        for (let i = 0; i < 14; i++) {
          const s = document.createElementNS(NS, 'path');
          const size = 6 + Math.random() * 8;
          s.setAttribute('d', 'M0 -1 L0.22 -0.22 L1 0 L0.22 0.22 L0 1 L-0.22 0.22 L-1 0 L-0.22 -0.22Z');
          s.setAttribute('fill', i % 3 ? '#e2c98f' : '#fff3e6');
          svg.appendChild(s);
          const a = (Math.PI * 2 * i) / 14 + Math.random() * 0.4;
          const dist = 60 + Math.random() * 90;
          s.animate(
            [
              { transform: `translate(${cx}px, ${cy}px) scale(0) rotate(0deg)`, opacity: 1 },
              { transform: `translate(${cx + Math.cos(a) * dist}px, ${cy + Math.sin(a) * dist}px) scale(${size}) rotate(90deg)`, opacity: 0 },
            ],
            { duration: 900 + Math.random() * 300, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' }
          );
        }
      }, 450);
      setTimeout(() => svg.remove(), 1700);
    }
  }
  if (!customElements.get('glow-finder-toggle')) customElements.define('glow-finder-toggle', GlowFinderToggle);

  /* ---------- Flying ribbons (voice notes): words run along the waving ribbons ---------- */
  const initFlyingRibbons = (root = document) => {
    root.querySelectorAll('[data-flying-ribbons]').forEach((svg) => {
      if (svg.dataset.bound) return;
      svg.dataset.bound = 'true';
      if (reduceMotion) { svg.pauseAnimations?.(); return; }
      const lanes = [...svg.querySelectorAll('textPath[data-copy]')].map((tp) => {
        const path = document.getElementById((tp.getAttribute('href') || '').slice(1));
        // Length of one copy of the text, measured once in the ribbon's font.
        const probe = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        probe.textContent = tp.dataset.copy;
        tp.parentNode.parentNode.appendChild(probe);
        const len = probe.getComputedTextLength() || 300;
        probe.remove();
        // Enough copies to cover the whole ribbon plus one copy of run-up.
        const total = (path?.getTotalLength?.() || 760) + len;
        tp.textContent = tp.dataset.copy.repeat(Math.ceil(total / len) + 1);
        const dir = Number(tp.dataset.dir) || -1;
        return { tp, len, dir, offset: dir < 0 ? 0 : -len };
      });
      const speed = 28; // svg units per second
      let last = 0;
      let running = false;
      const tick = (now) => {
        if (!running) return;
        const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
        last = now;
        lanes.forEach((l) => {
          l.offset += l.dir * speed * dt;
          if (l.offset <= -l.len) l.offset += l.len;
          if (l.offset > 0) l.offset -= l.len;
          l.tp.setAttribute('startOffset', l.offset.toFixed(2));
        });
        requestAnimationFrame(tick);
      };
      const io = new IntersectionObserver((entries) => {
        const on = entries.some((e) => e.isIntersecting);
        if (on && !running) { running = true; last = 0; svg.unpauseAnimations?.(); requestAnimationFrame(tick); }
        if (!on && running) { running = false; svg.pauseAnimations?.(); }
      });
      io.observe(svg);
    });
  };

  /* ---------- Add to cart the way Dawn's product form does it: the cart drawer updates and opens ----------
     beforeOpen runs after a successful add, just before the drawer opens (e.g. close a modal sheet first,
     otherwise the open <dialog> would keep the drawer inert). */
  const glowAddToCart = async (variantId, quantity = 1, { returnFocusTo, beforeOpen } = {}) => {
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', quantity);
    if (cart) {
      body.append('sections', cart.getSectionsToRender().map((s) => s.id));
      body.append('sections_url', window.location.pathname);
      cart.setActiveElement(returnFocusTo || document.activeElement);
    }
    const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body,
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || data.message || '');
    beforeOpen?.();
    if (!cart) {
      window.location = window.routes?.cart_url || '/cart';
      return data;
    }
    cart.classList.remove('is-empty');
    cart.renderContents(data);
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'glow', productVariantId: String(variantId), cartData: data });
    }
    return data;
  };

  /* ---------- Cross-sell cards: "+" adds (one-variant products) or opens the shade sheet ---------- */
  const initCrossSell = (root = document) => {
    root.querySelectorAll('[data-xsell]').forEach((wrap) => {
      if (wrap.dataset.bound) return;
      wrap.dataset.bound = 'true';
      const status = wrap.querySelector('[data-xsell-status]');
      let opener = null;

      wrap.querySelectorAll('[data-xsell-open]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const dialog = document.getElementById(btn.getAttribute('aria-controls'));
          if (!dialog || typeof dialog.showModal !== 'function') {
            window.location.href = btn.dataset.url; // very old browsers: go to the product page
            return;
          }
          opener = btn;
          dialog.showModal();
          document.documentElement.classList.add('glow-no-scroll');
        });
      });

      wrap.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('glow-no-scroll');
          const err = dialog.querySelector('[data-xsell-error]');
          if (err) err.hidden = true;
        });
        // A tap on the dimmed backdrop (outside the sheet) closes it.
        dialog.addEventListener('click', (e) => {
          if (e.target === dialog) dialog.close();
        });
        dialog.querySelector('[data-xsell-close]')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('change', (e) => {
          if (e.target.name !== 'id') return;
          const shade = dialog.querySelector('[data-xsell-shade]');
          const note = dialog.querySelector('[data-xsell-note]');
          if (shade) shade.textContent = e.target.dataset.name;
          if (note) note.textContent = e.target.dataset.note || '';
        });
      });

      wrap.querySelectorAll('form[data-xsell-form]').forEach((form) => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          const btn = form.querySelector('[type="submit"]');
          const dialog = form.closest('dialog');
          const err = form.querySelector('[data-xsell-error]') || status;
          const id = new FormData(form).get('id');
          if (!id || btn.getAttribute('aria-busy') === 'true') return;
          btn.setAttribute('aria-busy', 'true');
          if (err) err.hidden = true;
          try {
            await glowAddToCart(id, 1, {
              returnFocusTo: dialog ? opener : btn,
              beforeOpen: () => dialog?.close(),
            });
          } catch (error) {
            if (err) {
              err.textContent = error.message || wrap.dataset.error;
              err.hidden = false;
            }
          } finally {
            btn.removeAttribute('aria-busy');
          }
        });
      });
    });
  };

  /* ---------- Sticky "Add to cart" bar (product pages) ---------- */
  class GlowStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.info = document.querySelector('product-info[id^="MainProduct"]');
      const target = this.info?.querySelector('.product-form__buttons') || this.mainButton();
      if (!target || !('IntersectionObserver' in window)) return;
      try {
        this.variants = JSON.parse(this.querySelector('[data-sticky-variants]')?.textContent || '{}');
      } catch (e) {
        this.variants = {};
      }
      this.btn = this.querySelector('[data-sticky-add]');
      this.btn?.addEventListener('click', () => {
        const main = this.mainButton();
        if (!main || main.disabled || main.getAttribute('aria-disabled') === 'true') return;
        main.click();
      });
      document.documentElement.classList.add('glow-has-sticky');
      // Show once the main buttons are scrolled past (above the screen), not before reaching them. The observed
      // area reaches far below the screen, so "not intersecting" always means "above", even after a jump.
      this.io = new IntersectionObserver(
        ([entry]) => {
          const past = !entry.isIntersecting;
          this.classList.toggle('is-visible', past);
          this.inert = !past;
        },
        { rootMargin: '0px 0px 100000px 0px' }
      );
      this.io.observe(target);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      document.documentElement.classList.remove('glow-has-sticky');
      this.bound = false;
    }

    mainButton() {
      return this.info?.querySelector('product-form button[type="submit"]');
    }

    update(variantId) {
      const v = this.variants?.[variantId];
      if (!v) return;
      const shade = this.querySelector('[data-sticky-shade]');
      const price = this.querySelector('[data-sticky-price]');
      const dot = this.querySelector('[data-sticky-dot]');
      if (shade) shade.textContent = v.title;
      if (price) price.textContent = v.price;
      if (dot) dot.style.setProperty('--swatch', v.color || 'transparent');
      if (this.btn) {
        this.btn.disabled = !v.available;
        this.btn.textContent = v.available ? this.dataset.add : this.dataset.soldOut;
      }
    }
  }
  if (!customElements.get('glow-sticky-atc')) customElements.define('glow-sticky-atc', GlowStickyAtc);

  /* ---------- Shade splash: picking a shade spills its colour over the page's burgundy ----------
     The three colours come from the shade: "surface" (header, footer, buttons, arches) is the shade darkened just
     enough for cream text on it (contrast 4.8), "ink" (text) darker still, "deep" for the darkest band. The change
     is revealed through a paint splat growing from the tapped dot (View Transitions API), with a wet wash of the
     real shade that dries afterwards. No View Transitions: colours change with the wash only. Reduced motion:
     colours change at once. CSS in glow-base.css ("Shade splash"). Off: Theme settings > GlowDeer. */
  const CREAM = [255, 251, 245];
  const BEIGE = [247, 240, 230];
  const parseColor = (value) => {
    const v = String(value || '').trim();
    let m = v.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (m) {
      let h = m[1];
      if (h.length === 3) h = h.split('').map((c) => c + c).join('');
      const n = parseInt(h, 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    m = v.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
  };
  const luminance = (rgb) => {
    const [r, g, b] = rgb.map((c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const contrast = (a, b) => {
    const la = luminance(a);
    const lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  };
  const rgbToHsl = ([r, g, b]) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    return [h / 6, s, l];
  };
  const hslToRgb = ([h, s, l]) => {
    if (!s) return [l, l, l].map((v) => Math.round(v * 255));
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue = (t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    return [hue(h + 1 / 3), hue(h), hue(h - 1 / 3)].map((v) => Math.round(v * 255));
  };
  // Darken (same hue and saturation) until the colour reaches the contrast ratio against a light background.
  const deepen = (rgb, against, ratio) => {
    const [h, s] = rgbToHsl(rgb);
    let l = rgbToHsl(rgb)[2];
    let out = rgb;
    while (contrast(out, against) < ratio && l > 0.02) {
      l = Math.max(0, l - 0.015);
      out = hslToRgb([h, s, l]);
    }
    return out;
  };

  let lastPointer = null;
  document.addEventListener('pointerdown', (e) => {
    lastPointer = { x: e.clientX, y: e.clientY, t: performance.now() };
  }, { passive: true, capture: true });
  const originOf = (el) => {
    if (lastPointer && performance.now() - lastPointer.t < 1500) return [lastPointer.x, lastPointer.y];
    const r = el?.getBoundingClientRect?.();
    return r ? [r.left + r.width / 2, r.top + r.height / 2] : [innerWidth / 2, innerHeight / 2];
  };

  // The moving banner and the logo bar live at the top of the page. When a shade is picked further down, they are
  // pinned to the top of the screen first (sticky, so nothing below moves) so the colour is seen spilling over them.
  // They let go once the visitor scrolls on. Returns how long to wait for their slide-in (ms).
  const showTopBars = () => {
    const root = document.documentElement;
    const header = document.querySelector('.section-header');
    if (!header || root.classList.contains('glow-bars-pinned')) return 0;
    const marquee = document.querySelector('.shopify-section-group-header-group .glow-marquee')?.closest('.shopify-section');
    const sticky = document.querySelector('sticky-header');
    const onScreen = (marquee || header).getBoundingClientRect().top > -2 && !header.classList.contains('shopify-section-header-hidden');
    if (onScreen) return 0;
    if (marquee) {
      root.style.setProperty('--glow-annc-h', `${marquee.offsetHeight}px`);
      marquee.classList.remove('glow-bar-leaving');
      marquee.classList.add('glow-bar-pinned');
    }
    header.classList.add('glow-header-pinned');
    if (sticky) {
      sticky.preventHide = true;
      sticky.reveal?.();
    }
    root.classList.add('glow-bars-pinned');
    const fromY = scrollY;
    const since = performance.now();
    const release = () => {
      window.removeEventListener('scroll', onScroll);
      if (sticky) sticky.preventHide = false;
      const done = () => {
        root.classList.remove('glow-bars-pinned');
        marquee?.classList.remove('glow-bar-pinned', 'glow-bar-leaving');
        header.classList.remove('glow-header-pinned');
      };
      if (marquee && !reduceMotion) {
        marquee.classList.add('glow-bar-leaving');
        setTimeout(done, 260);
      } else done();
    };
    const onScroll = () => {
      if (performance.now() - since > 1600 && Math.abs(scrollY - fromY) > 80) release();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return reduceMotion ? 0 : 320;
  };

  let splashWash = null;
  let splashTimer = null;
  const splashTo = (value, [x, y]) => {
    const root = document.documentElement;
    if (!root.hasAttribute('data-shade-splash')) return;
    const rgb = parseColor(value);
    if (!rgb) return;
    const surface = deepen(rgb, CREAM, 4.8);
    const key = surface.join(',');
    if (root.dataset.shadeNow === key) return; // same colour again: nothing to spill
    const ink = deepen(rgb, BEIGE, 9);
    const deep = deepen(rgb, CREAM, 13);
    if (!splashWash) {
      splashWash = document.createElement('div');
      splashWash.className = 'glow-splash-wash';
      splashWash.setAttribute('aria-hidden', 'true');
      document.body.appendChild(splashWash);
    }
    const wash = splashWash;
    const apply = () => {
      root.style.setProperty('--glow-shade', key);
      root.style.setProperty('--glow-shade-ink', ink.join(','));
      root.style.setProperty('--glow-shade-deep', deep.join(','));
      root.style.setProperty('--glow-shade-raw', rgb.join(','));
      root.classList.add('glow-shaded');
      root.dataset.shadeNow = key;
    };
    clearTimeout(splashTimer); // a newer pick wins over one still waiting for the bars
    const lead = showTopBars();
    if (reduceMotion) {
      apply();
      return;
    }
    if (lead) {
      splashTimer = setTimeout(() => spill(), lead);
      return;
    }
    spill();

    // Paint spill: View Transitions reveal through a growing splat (or the wash alone on older browsers).
    function spill() {
      wash.style.setProperty('--wash-x', `${x}px`);
      wash.style.setProperty('--wash-y', `${y}px`);
      const dry = () => requestAnimationFrame(() => wash.classList.remove('is-wet'));

      if (typeof document.startViewTransition !== 'function') {
        // Older browsers: the wash splashes in from the dot, the colours change under it, then it dries.
        apply();
        wash.classList.add('is-wet');
        wash.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${Math.hypot(innerWidth, innerHeight)}px at ${x}px ${y}px)`] },
          { duration: 700, easing: 'cubic-bezier(.3,.9,.25,1)' }
        ).finished.then(dry, dry);
        return;
      }

      root.classList.add('glow-splashing');
      const vt = document.startViewTransition(() => {
        apply();
        wash.classList.add('is-wet');
      });
      let reveal = null;
      vt.ready
        .then(() => {
          // The splat's solid core reaches ~23% of its size from the centre: big enough to cover the far corner.
          const far = Math.max(Math.hypot(x, y), Math.hypot(innerWidth - x, y), Math.hypot(x, innerHeight - y), Math.hypot(innerWidth - x, innerHeight - y));
          const size = Math.ceil(far / 0.22);
          const frames = {
            maskSize: ['0px 0px', `${size}px ${size}px`],
            maskPosition: [`${x}px ${y}px`, `${x - size / 2}px ${y - size / 2}px`],
            webkitMaskSize: ['0px 0px', `${size}px ${size}px`],
            webkitMaskPosition: [`${x}px ${y}px`, `${x - size / 2}px ${y - size / 2}px`],
          };
          reveal = root.animate(frames, {
            duration: 950,
            easing: 'cubic-bezier(.3,.9,.25,1)',
            pseudoElement: '::view-transition-new(root)',
            fill: 'forwards',
          });
        })
        .catch(() => {});
      vt.finished.finally(() => {
        reveal?.cancel(); // the pseudo-element is gone; don't keep a finished animation around
        root.classList.remove('glow-splashing');
        dry();
      });
    }
  };

  // Every place a shade can be picked: product-page circles, shade finder dots, quick-add sheet dots.
  // Product pages only (Abbas, 2026-10-11): layout/theme.liquid sets data-shade-splash just there, so the
  // homepage shade finder never recolours the site.
  document.addEventListener('click', (e) => {
    const dot = e.target.closest?.('.glow-swatch[data-swatch]');
    if (dot) splashTo(dot.dataset.swatch, originOf(dot));
  });
  document.addEventListener('change', (e) => {
    if (!e.isTrusted) return; // the compare's own sync of the product form is not a new pick
    const t = e.target;
    if (t.matches?.('.swatch-input__input')) {
      const chip = document.querySelector(`label[for="${CSS.escape(t.id)}"] .swatch`);
      splashTo(chip?.style.getPropertyValue('--swatch--background'), originOf(chip));
    } else if (t.matches?.('.glow-dot__input')) {
      const chip = t.nextElementSibling;
      splashTo(chip?.style.getPropertyValue('--swatch'), originOf(chip));
    }
  });

  /* ---------- Free-delivery bar in the cart (snippets/glow-free-delivery.liquid) ----------
     Dawn re-renders the cart drawer and the cart page after every change, so each bar is a new element. Before it is
     painted (MutationObserver callbacks run before the next frame) it gets the previous fill as its starting point,
     so the bar grows from where it was instead of from zero. */
  const initFreeDelivery = () => {
    let last = null;
    const prep = (bar) => {
      const pct = parseFloat(bar.dataset.pct) || 0;
      if (last !== null) bar.style.setProperty('--glow-fs-from', `${last}%`);
      last = pct;
    };
    document.querySelectorAll('.glow-freeship').forEach(prep);
    const observer = new MutationObserver((records) => {
      records.forEach((record) =>
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches('.glow-freeship')) prep(node);
          node.querySelectorAll('.glow-freeship').forEach(prep);
        })
      );
    });
    document.querySelectorAll('cart-drawer, cart-items').forEach((el) =>
      observer.observe(el, { childList: true, subtree: true })
    );
  };

  const init = (root) => {
    initFlyingRibbons(root);
    initReveal(root);
    initDragRails(root);
    initVoiceJump(document);
    initCountdowns(root);
    initFinderJump(root);
    initCrossSell(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init(document);
      initFreeDelivery();
    });
  } else {
    init(document);
    initFreeDelivery();
  }
  // Dawn's pubsub.js may load after this file, so wait for everything before subscribing.
  if (document.readyState === 'complete') initShadeNotes();
  else window.addEventListener('load', initShadeNotes);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
